
import { developmentDetector } from './developmentModeDetector';

/**
 * Centralized Service Worker Manager - Silent Mode
 * Prevents duplicate registrations and conflicts with minimal logging
 */
class ServiceWorkerManager {
  private isRegistered = false;
  private registration: ServiceWorkerRegistration | null = null;

  async initialize(): Promise<ServiceWorkerRegistration | null> {
    const config = developmentDetector.getConfig();
    
    // Skip in development with silent cleanup
    if (config.isDevelopment) {
      await this.cleanupDevelopmentWorkers();
      return null;
    }

    // Skip service worker for authenticated/admin areas to prevent offline caching issues
    const currentPath = window.location.pathname;
    const skipPaths = ['/admin', '/my-profile', '/checkout', '/payment', '/auth'];
    const shouldSkip = skipPaths.some(path => currentPath.startsWith(path));
    
    if (shouldSkip) {
      await this.cleanupDevelopmentWorkers();
      return null;
    }

    // Skip if already registered
    if (this.isRegistered && this.registration) {
      return this.registration;
    }

    // Check if service workers are supported
    if (!('serviceWorker' in navigator)) {
      return null;
    }

    try {
      this.registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none'
      });

      this.setupUpdateHandling();
      this.isRegistered = true;
      
      return this.registration;
    } catch (error) {
      // Silent failure in production
      return null;
    }
  }

  private async cleanupDevelopmentWorkers(): Promise<void> {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      const unregisterPromises = registrations.map(registration => 
        registration.unregister().catch(() => {
          // Silent cleanup
        })
      );
      await Promise.all(unregisterPromises);
      
      // No logging - silent cleanup
    } catch (error) {
      // Silent failure
    }
  }

  private setupUpdateHandling(): void {
    if (!this.registration) return;

    this.registration.addEventListener('updatefound', () => {
      const newWorker = this.registration?.installing;
      if (newWorker) {
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New service worker installed, prompt user to refresh
            if (window.confirm('A new version is available. Refresh to update?')) {
              window.location.reload();
            }
          }
        });
      }
    });
  }

  getRegistration(): ServiceWorkerRegistration | null {
    return this.registration;
  }

  isServiceWorkerRegistered(): boolean {
    return this.isRegistered;
  }
}

export const serviceWorkerManager = new ServiceWorkerManager();
